import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { MessagePageContext, MessagesPortalSettings } from '@oneup/shared';
import { useOrganization } from '../organization/OrganizationProvider';
import { useClientPageContext } from '../platform/page-context';
import { createThread, fetchMessagesSettings, fetchNotifications, sendHeartbeat } from './messages-api';
import { buildSendKey, createSendGuard } from './send-command';

/**
 * Messaging state for the Alice sidebar: which mode the panel is in
 * (Alice / Messages / a specific thread), the unread badge, portal settings
 * gating, `?thread=` deep-link handling, and the lightweight activity
 * heartbeat used server-side for notification delay/suppression decisions.
 */

export type SidebarMode = 'alice' | 'messages' | 'thread';

interface MessagesContextValue {
  settings: MessagesPortalSettings | null;
  mode: SidebarMode;
  activeThreadId: string | null;
  /** Draft text carried from the Alice composer's /send command. */
  draftText: string;
  /** Persisted Alice composer draft, preserved across mode switches. */
  aliceDraft: string;
  setAliceDraft: (value: string) => void;
  unreadCount: number;
  /** Bumps whenever thread data changed and lists should refetch. */
  refreshToken: number;
  openMessages: (draft?: string, options?: { compose?: boolean }) => void;
  /** Which Messages sub-view to show on entry: the thread list or the composer. */
  messagesView: 'list' | 'compose';
  openThread: (threadId: string) => void;
  backToAlice: () => void;
  backToMessages: () => void;
  bumpRefresh: () => void;
  setUnreadCount: (count: number) => void;
  /** Approved context for the page the user is currently viewing. */
  currentPageContext: () => MessagePageContext;
  /**
   * `/send <message>` fast path: create a client-wide thread with the message,
   * auto-link the current record (unless `linkContext` is false), and return
   * the new thread id. Idempotent — a duplicate submit resolves to null without
   * creating a second thread. Returns null when there is nothing to send.
   */
  sendQuick: (text: string, options?: { linkContext?: boolean }) => Promise<string | null>;
  sendQuickPending: boolean;
  /** Signals the panel shell to expand (set by deep links). */
  forceOpenToken: number;
}

const MessagesContext = createContext<MessagesContextValue | null>(null);

const HEARTBEAT_INTERVAL_MS = 60_000;

const MODULE_ENTITY_TYPES: Record<string, string> = {
  work: 'task',
  websites: 'website',
  forms: 'form',
  docs: 'document',
  organizations: 'organization',
};

export function MessagesProvider({
  children,
  onForceOpen,
}: {
  children: ReactNode;
  onForceOpen?: () => void;
}) {
  const organization = useOrganization();
  const { selectedClientId } = organization;
  const selectedRole = (organization.organizations ?? []).find(
    (item) => item.clientId === selectedClientId,
  )?.role;
  const readOnly = selectedRole === 'viewer';
  const pageContext = useClientPageContext();
  const location = useLocation();
  const navigate = useNavigate();

  const [settings, setSettings] = useState<MessagesPortalSettings | null>(null);
  const [mode, setMode] = useState<SidebarMode>('alice');
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [draftText, setDraftText] = useState('');
  const [aliceDraft, setAliceDraft] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);
  const [forceOpenToken, setForceOpenToken] = useState(0);
  const [messagesView, setMessagesView] = useState<'list' | 'compose'>('list');
  const [sendQuickPending, setSendQuickPending] = useState(false);
  const consumedDeepLink = useRef<string | null>(null);
  const sendGuard = useRef(createSendGuard());
  const forcePanelOpen = useCallback(() => {
    setForceOpenToken((token) => token + 1);
    onForceOpen?.();
  }, [onForceOpen]);

  // Portal settings gate the whole feature; fail quiet (feature hidden).
  // Deferred past the shell's boot burst so the Worker's per-IP rate bucket
  // (4/sec) is left to the access/organization/module requests.
  useEffect(() => {
    if (!selectedClientId) return;
    let active = true;
    const handle = window.setTimeout(() => {
      void fetchMessagesSettings(selectedClientId)
        .then((next) => {
          if (active) setSettings(readOnly ? {
            ...next,
            clientCanStartThreads: false,
            clientCanReply: false,
          } : next);
        })
        .catch(() => {
          if (active) setSettings(null);
        });
    }, 1500);
    return () => {
      active = false;
      window.clearTimeout(handle);
    };
  }, [readOnly, selectedClientId]);

  // Deep link: ?thread=<id> opens the exact thread with the sidebar expanded.
  useEffect(() => {
    const threadId = new URLSearchParams(location.search).get('thread');
    if (!threadId || consumedDeepLink.current === threadId) return;
    consumedDeepLink.current = threadId;
    setActiveThreadId(threadId);
    setMode('thread');
    forcePanelOpen();
  }, [forcePanelOpen, location.search]);

  // Heartbeat + unread badge. One interval covers both; window focus events
  // send an immediate beat so read/active state stays fresh.
  useEffect(() => {
    if (!selectedClientId || !settings?.messagesEnabled) return;
    let active = true;
    const beat = (focused?: boolean) => {
      if (!readOnly) {
        void sendHeartbeat(selectedClientId, {
          route: location.pathname,
          threadId: mode === 'thread' ? activeThreadId : null,
          focused: focused ?? (typeof document !== 'undefined' ? !document.hidden : undefined),
        }).catch(() => undefined);
      }
      void fetchNotifications(selectedClientId, { eventType: 'new_message', limit: 100 })
        .then((response) => {
          if (active) setUnreadCount(response.unreadCount);
        })
        .catch(() => undefined);
    };
    // First beat is delayed so page-load bursts stay under the API rate bucket.
    const initial = window.setTimeout(() => beat(), 4000);
    // Skip scheduled beats in a hidden tab — presence there is already "not
    // viewing", and the focus/visibility handlers cover the moment they return.
    const interval = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      beat();
    }, HEARTBEAT_INTERVAL_MS);
    const onFocus = () => beat(true);
    const onBlur = () => beat(false);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    return () => {
      active = false;
      window.clearTimeout(initial);
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, [selectedClientId, settings?.messagesEnabled, mode, activeThreadId, location.pathname, readOnly]);

  const clearThreadParam = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (params.has('thread')) {
      params.delete('thread');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const openMessages = useCallback((draft = '', options?: { compose?: boolean }) => {
    setDraftText(draft);
    setActiveThreadId(null);
    setMode('messages');
    // The tab opens the list; only an explicit compose intent (or a carried
    // draft) opens the composer.
    setMessagesView(options?.compose || draft.trim() ? 'compose' : 'list');
    forcePanelOpen();
  }, [forcePanelOpen]);

  const openThread = useCallback((threadId: string) => {
    setActiveThreadId(threadId);
    setMode('thread');
    forcePanelOpen();
  }, [forcePanelOpen]);

  const backToAlice = useCallback(() => {
    setMode('alice');
    setActiveThreadId(null);
    setDraftText('');
    // Alice entry points live throughout the portal, including the overview's
    // contextual briefing action. Returning to Alice must therefore also make
    // a collapsed desktop rail or dismissed mobile overlay visible again.
    forcePanelOpen();
    clearThreadParam();
  }, [clearThreadParam, forcePanelOpen]);

  const backToMessages = useCallback(() => {
    setMode('messages');
    setActiveThreadId(null);
    setMessagesView('list');
    clearThreadParam();
  }, [clearThreadParam]);

  const currentPageContext = useCallback((): MessagePageContext => {
    const entityType = pageContext.moduleId
      ? MODULE_ENTITY_TYPES[pageContext.moduleId]
      : undefined;
    return {
      pageType: pageContext.moduleId,
      moduleId: pageContext.moduleId,
      moduleLabel: pageContext.moduleLabel,
      entityType: pageContext.recordId ? entityType : undefined,
      entityId: pageContext.recordId,
      entityLabel: pageContext.recordLabel,
      route: location.pathname,
    };
  }, [pageContext, location.pathname]);

  const bumpRefresh = useCallback(() => setRefreshToken((token) => token + 1), []);

  const sendQuick = useCallback(
    async (text: string, options?: { linkContext?: boolean }): Promise<string | null> => {
      const body = text.trim();
      if (!selectedClientId || !body || readOnly) return null;
      const key = buildSendKey(selectedClientId, body);
      // Idempotency: ignore a duplicate submit (double Enter/click or fast resend).
      if (!sendGuard.current.tryBegin(key)) return null;
      setSendQuickPending(true);
      try {
        const context = options?.linkContext === false ? undefined : currentPageContext();
        const result = await createThread(selectedClientId, { body, context });
        sendGuard.current.end(key);
        bumpRefresh();
        return result.thread.id;
      } catch (error) {
        sendGuard.current.release(key); // failed send — allow an immediate retry
        throw error;
      } finally {
        setSendQuickPending(false);
      }
    },
    [selectedClientId, currentPageContext, bumpRefresh, readOnly],
  );

  const value = useMemo<MessagesContextValue>(
    () => ({
      settings,
      mode,
      activeThreadId,
      draftText,
      aliceDraft,
      setAliceDraft,
      unreadCount,
      refreshToken,
      openMessages,
      openThread,
      backToAlice,
      backToMessages,
      bumpRefresh,
      setUnreadCount,
      currentPageContext,
      sendQuick,
      sendQuickPending,
      messagesView,
      forceOpenToken,
    }),
    [
      settings,
      mode,
      activeThreadId,
      draftText,
      aliceDraft,
      unreadCount,
      refreshToken,
      messagesView,
      openMessages,
      openThread,
      backToAlice,
      backToMessages,
      bumpRefresh,
      currentPageContext,
      sendQuick,
      sendQuickPending,
      forceOpenToken,
    ],
  );

  return <MessagesContext.Provider value={value}>{children}</MessagesContext.Provider>;
}

export function useMessages(): MessagesContextValue {
  const value = useContext(MessagesContext);
  if (!value) throw new Error('useMessages must be used within a MessagesProvider.');
  return value;
}
